
import * as React from 'react';
import Layout from '../../components/layout';
import { useEffect, useState } from 'react';
import { 
    Box, 
    Grid, 
    Card, 
    CardContent, 
    Typography, 
    CardActionArea,
    Chip,
    CircularProgress,
    Alert,
    useMediaQuery,
    useTheme,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
} from '@mui/material';
import Header from '../../components/header';
import { Link } from 'react-router-dom';
import DescriptionIcon from '@mui/icons-material/Description';
import PublishIcon from '@mui/icons-material/Publish';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import SchoolIcon from '@mui/icons-material/School';
import AssignmentIcon from '@mui/icons-material/Assignment';
import GradeIcon from '@mui/icons-material/Grade';
import PersonIcon from '@mui/icons-material/Person';
import { getAssignmentsByInstitution, getStudentsByInstitution } from '../../utils/firebaseQueries';

const studentLinks = [
    { text: 'View Sessionals Marks', icon: <DescriptionIcon sx={{ fontSize: 32 }} />, href: '/check-sessional', color: '#FF6B6B' },
    { text: 'Submit Assignment', icon: <PublishIcon sx={{ fontSize: 32 }} />, href: '/submit-assignment', color: '#4ECDC4' },
    { text: 'Check Attendance', icon: <EventAvailableIcon sx={{ fontSize: 32 }} />, href: '/check-attendence', color: '#45B7D1' },
    { text: 'Check Final Marks', icon: <GradeIcon sx={{ fontSize: 32 }} />, href: '/checkmarks', color: '#FFA07A' },
    { text: 'View Assignments', icon: <AssignmentIcon sx={{ fontSize: 32 }} />, href: '/view-assignments', color: '#98D8C8' },
    { text: 'My Classes', icon: <SchoolIcon sx={{ fontSize: 32 }} />, href: '/my-classes', color: '#F7DC6F' },
];

function StudentDashboard() {
    const [studentData, setStudentData] = useState(null);
    const [stats, setStats] = useState({
        totalAssignments: 0,
        submittedAssignments: 0,
        avgAttendance: 0,
        totalMarks: 0,
    });
    const [recentAssignments, setRecentAssignments] = useState([]);
    const [classmates, setClassmates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        const fetchStudentData = async () => {
            try {
                setLoading(true);
                const loggedUser = JSON.parse(sessionStorage.getItem("logged_user"));
                
                if (!loggedUser || !loggedUser.institution_id || loggedUser.role !== 'student') {
                    setError('Invalid session. Please login again.');
                    setLoading(false);
                    return;
                }

                const instId = loggedUser.institution_id;

                // Set student data from logged user
                setStudentData({
                    id: loggedUser.id,
                    name: loggedUser.name,
                    email: loggedUser.email,
                    phone: loggedUser.phone || 'N/A',
                    profileId: loggedUser.profileId,
                    branch: loggedUser.branch || extractBranchFromProfileId(loggedUser.profileId),
                    status: loggedUser.status
                });

                // Fetch assignments for student's institution
                const assignments = await getAssignmentsByInstitution(instId);
                
                // Filter assignments for student's branch if applicable
                const studentBranch = loggedUser.branch || extractBranchFromProfileId(loggedUser.profileId);
                const relevantAssignments = assignments.filter(a => 
                    !a.branch || a.branch.toUpperCase() === studentBranch.toUpperCase()
                );

                setStats({
                    totalAssignments: relevantAssignments.length,
                    submittedAssignments: Math.floor(relevantAssignments.length * 0.3),
                    avgAttendance: 85,
                    totalMarks: 620
                });

                // Get recent assignments (sort by dueDate)
                const recent = relevantAssignments
                    .sort((a, b) => new Date(b.dueDate) - new Date(a.dueDate))
                    .slice(0, 5);
                setRecentAssignments(recent);

                // Fetch classmates (students with same branch)
                const allStudents = await getStudentsByInstitution(instId);
                const sameClassmates = allStudents.filter(s => 
                    extractBranchFromProfileId(s.profileId) === studentBranch &&
                    s.uid !== loggedUser.id
                ).slice(0, 5);
                setClassmates(sameClassmates);

            } catch (err) {
                console.error('Error fetching dashboard data:', err);
                setError('Failed to load dashboard. Please refresh the page.');
            } finally {
                setLoading(false);
            }
        };

        fetchStudentData();
    }, []);

    const extractBranchFromProfileId = (profileId) => {
        if (!profileId) return 'General';
        const parts = profileId.split('_');
        if (parts.length >= 2) {
            return parts[1].toUpperCase();
        }
        return 'General';
    };

    if (loading) {
        return (
            <Layout>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                    <CircularProgress />
                </Box>
            </Layout>
        );
    }

    if (error) {
        return (
            <Layout>
                <Header title="Student Dashboard" />
                <Box sx={{ p: 3 }}>
                    <Alert severity="error">{error}</Alert>
                </Box>
            </Layout>
        );
    }

    return (
        <Layout>
            <Header title="Student Dashboard" />
            
            {/* Welcome Section */}
            <Box sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                            Welcome, {studentData?.name}!
                        </Typography>
                        <Typography variant="body1" sx={{ opacity: 0.9 }}>
                            Branch: <strong>{studentData?.branch}</strong> | Email: <strong>{studentData?.email}</strong>
                        </Typography>
                    </Box>
                    <Box sx={{ mt: isMobile ? 2 : 0 }}>
                        <Chip label={studentData?.status === 'active' ? '✓ Active' : 'Inactive'} 
                              color={studentData?.status === 'active' ? 'success' : 'error'} />
                    </Box>
                </Box>
            </Box>

            {/* Quick Stats */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Total Assignments
                            </Typography>
                            <Typography variant="h5">
                                {stats.totalAssignments}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Submitted
                            </Typography>
                            <Typography variant="h5">
                                {stats.submittedAssignments}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Attendance
                            </Typography>
                            <Typography variant="h5">
                                {stats.avgAttendance}%
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Total Marks
                            </Typography>
                            <Typography variant="h5">
                                {stats.totalMarks}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Quick Links */}
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Quick Access
            </Typography>
            <Grid container spacing={2} sx={{ mb: 4 }}>
                {studentLinks.map((link, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                        <Link to={link.href} style={{ textDecoration: 'none' }}>
                            <Card sx={{ height: '100%', '&:hover': { boxShadow: 4, transform: 'translateY(-4px)' }, transition: 'all 0.3s' }}>
                                <CardActionArea>
                                    <CardContent sx={{ textAlign: 'center', py: 4 }}>
                                        <Box sx={{ color: link.color, mb: 2 }}>
                                            {link.icon}
                                        </Box>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                            {link.text}
                                        </Typography>
                                    </CardContent>
                                </CardActionArea>
                            </Card>
                        </Link>
                    </Grid>
                ))}
            </Grid>

            {/* Recent Assignments */}
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Recent Assignments
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 4 }}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableCell><strong>Title</strong></TableCell>
                            <TableCell><strong>Subject</strong></TableCell>
                            <TableCell><strong>Due Date</strong></TableCell>
                            <TableCell><strong>Marks</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {recentAssignments.length > 0 ? (
                            recentAssignments.map((assignment) => (
                                <TableRow key={assignment.id} hover>
                                    <TableCell>{assignment.title}</TableCell>
                                    <TableCell>{assignment.subject}</TableCell>
                                    <TableCell>{new Date(assignment.dueDate).toLocaleDateString()}</TableCell>
                                    <TableCell>{assignment.total_marks}</TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                                    No assignments found
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Classmates */}
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Your Classmates
            </Typography>
            <Grid container spacing={2}>
                {classmates.length > 0 ? (
                    classmates.map((classmate) => (
                        <Grid item xs={12} sm={6} md={4} key={classmate.uid}>
                            <Card>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <PersonIcon sx={{ fontSize: 40, color: '#667eea' }} />
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                {classmate.name}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                {classmate.email}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))
                ) : (
                    <Typography variant="body2" color="textSecondary" sx={{ py: 3 }}>
                        No classmates found
                    </Typography>
                )}
            </Grid>
        </Layout>
    );
}

export default StudentDashboard;